import React, { useState, useRef, useEffect, useCallback } from 'react';
import './SearchableDropdown.css';

/* ── Option types ────────────────────────────────────────────── */

export interface DropdownOption {
  value: string;
  label: string;
  /** Depth for hierarchical indentation (0 = no indent) */
  depth?: number;
  /** Group header this option belongs to (rendered as a section label) */
  group?: string;
}

export interface SearchableDropdownProps {
  options: DropdownOption[];
  /** Selected value (single-select mode). Optional in multiSelect mode. */
  value?: string;
  /** Change handler (single-select mode). Optional in multiSelect mode. */
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Extra class on the outer wrapper (so parent CSS can target width, etc.) */
  wrapperClassName?: string;
  /** If true, show error styling */
  hasError?: boolean;
  disabled?: boolean;
  /** Multi-select mode: clicking an option toggles it (dropdown stays open) and
   *  selected options are highlighted. Use with selectedValues + onToggleValue
   *  instead of value + onChange. */
  multiSelect?: boolean;
  /** Selected values in multiSelect mode (pass options pre-ordered if you want
   *  selected pinned to the top). */
  selectedValues?: string[];
  /** Toggle handler in multiSelect mode. */
  onToggleValue?: (value: string) => void;
  /** Optional pinned action row shown at the TOP of the open dropdown (e.g.
   *  "+ Add new folder"). It is NOT part of the options list and is not filtered
   *  by the search box — clicking it closes the dropdown and runs onClick. */
  actionRow?: { label: React.ReactNode; onClick: () => void };
}

/* ── Component ───────────────────────────────────────────────── */

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value = '',
  onChange,
  placeholder = '-- Select --',
  className = '',
  wrapperClassName = '',
  hasError = false,
  disabled = false,
  multiSelect = false,
  selectedValues = [],
  onToggleValue,
  actionRow,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  /* ── Derived: selected label ────────────────────────────────── */
  const selectedOption = options.find(o => o.value === value);
  const displayLabel = selectedOption?.label ?? '';

  /* ── Multi-select derived state ─────────────────────────────── */
  const isOptionSelected = useCallback(
    (v: string) => multiSelect ? selectedValues.includes(v) : v === value,
    [multiSelect, selectedValues, value],
  );
  const multiCount = selectedValues.length;
  const multiSummary = multiCount === 0
    ? ''
    : multiCount === 1
      ? (options.find(o => o.value === selectedValues[0])?.label ?? '1 selected')
      : `${multiCount} selected`;
  // Closed-state text + whether a value exists (unifies single + multi).
  const hasValue = multiSelect ? multiCount > 0 : !!value;
  const closedText = multiSelect ? multiSummary : displayLabel;

  /* ── Derived: filtered options ──────────────────────────────── */
  const filtered = search.trim()
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  /* ── Close on outside click ─────────────────────────────────── */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
        setHighlightIdx(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ── Scroll highlighted item into view ──────────────────────── */
  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('.sd-option');
      items[highlightIdx]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  /* ── Select a value ─────────────────────────────────────────── */
  const selectValue = useCallback((val: string) => {
    onChange?.(val);
    setIsOpen(false);
    setSearch('');
    setHighlightIdx(-1);
  }, [onChange]);

  /* ── Open the dropdown ──────────────────────────────────────── */
  // `seed` pre-fills the search box so a keyboard user can Tab in and start
  // typing immediately (the first character opens + filters). Empty = plain open.
  const openWithSearch = useCallback((seed: string) => {
    if (disabled) return;
    setIsOpen(true);
    setSearch(seed);
    setHighlightIdx(seed ? 0 : -1);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [disabled]);
  const open = useCallback(() => openWithSearch(''), [openWithSearch]);

  /* ── Keyboard navigation ────────────────────────────────────── */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(prev => (prev < filtered.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(prev => (prev > 0 ? prev - 1 : filtered.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIdx >= 0 && highlightIdx < filtered.length) {
        if (multiSelect) {
          onToggleValue?.(filtered[highlightIdx].value);   // toggle, keep open
        } else {
          selectValue(filtered[highlightIdx].value);
        }
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearch('');
      setHighlightIdx(-1);
    }
  };

  /* ── Build grouped rendering ────────────────────────────────── */
  const renderOptions = () => {
    if (filtered.length === 0) {
      return <div className="sd-no-results">No matches found</div>;
    }

    const hasGroups = filtered.some(o => o.group);

    if (hasGroups) {
      // Render grouped options
      const groups: { label: string; items: { opt: DropdownOption; globalIdx: number }[] }[] = [];
      const ungrouped: { opt: DropdownOption; globalIdx: number }[] = [];
      let globalIdx = 0;

      filtered.forEach(opt => {
        if (opt.group) {
          let grp = groups.find(g => g.label === opt.group);
          if (!grp) {
            grp = { label: opt.group!, items: [] };
            groups.push(grp);
          }
          grp.items.push({ opt, globalIdx });
        } else {
          ungrouped.push({ opt, globalIdx });
        }
        globalIdx++;
      });

      return (
        <>
          {ungrouped.map(({ opt, globalIdx: gi }) => renderSingleOption(opt, gi))}
          {groups.map(grp => (
            <React.Fragment key={grp.label}>
              <div className="sd-group-label">{grp.label}</div>
              {grp.items.map(({ opt, globalIdx: gi }) => renderSingleOption(opt, gi))}
            </React.Fragment>
          ))}
        </>
      );
    }

    // No groups — flat list
    return filtered.map((opt, idx) => renderSingleOption(opt, idx));
  };

  const renderSingleOption = (opt: DropdownOption, idx: number) => {
    const isHighlighted = idx === highlightIdx;
    const isSelected = isOptionSelected(opt.value);
    const indent = opt.depth ? opt.depth * 16 : 0;

    return (
      <div
        key={opt.value}
        className={`sd-option ${multiSelect ? 'sd-option-multi' : ''} ${isHighlighted ? 'sd-option-highlighted' : ''} ${isSelected ? 'sd-option-selected' : ''}`}
        style={indent ? { paddingLeft: 12 + indent } : undefined}
        onMouseEnter={() => setHighlightIdx(idx)}
        onMouseDown={(e) => {
          e.preventDefault(); // keep focus on input
          if (multiSelect) {
            onToggleValue?.(opt.value);                 // toggle; dropdown stays open
            setTimeout(() => inputRef.current?.focus(), 0);
          } else {
            selectValue(opt.value);
          }
        }}
      >
        {multiSelect ? (
          <>
            <span className="sd-option-label">{opt.label}</span>
            {isSelected && <span className="sd-option-check">✓</span>}
          </>
        ) : opt.label}
      </div>
    );
  };

  /* ── Render ─────────────────────────────────────────────────── */
  return (
    <div
      className={`sd-wrapper ${wrapperClassName} ${hasError ? 'sd-error' : ''} ${disabled ? 'sd-disabled' : ''}`}
      ref={wrapperRef}
    >
      {/* Closed state: looks like a select */}
      {!isOpen ? (
        <div
          className={`sd-display ${className}`}
          onClick={open}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
              e.preventDefault();
              open();
            } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
              // Type-to-search: any printable character opens the dropdown and seeds
              // the filter with it, so a keyboard user who Tabs in can just start typing.
              e.preventDefault();
              openWithSearch(e.key);
            }
          }}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className={hasValue ? 'sd-display-text' : 'sd-display-placeholder'}>
            {hasValue ? closedText : placeholder}
          </span>
          <span className="sd-chevron">▾</span>
        </div>
      ) : (
        /* Open state: text input with dropdown */
        <>
          <div className={`sd-input-row ${className}`}>
            <input
              ref={inputRef}
              className="sd-search-input"
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setHighlightIdx(0); }}
              onKeyDown={handleKeyDown}
              placeholder={hasValue ? closedText : placeholder}
              autoComplete="off"
            />
            {/* Clear / close button (single-select only) */}
            {!multiSelect && value && (
              <button
                className="sd-clear-btn"
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectValue('');
                }}
                title="Clear selection"
                type="button"
              >
                ✕
              </button>
            )}
            <span className="sd-chevron sd-chevron-open">▴</span>
          </div>
          <div className="sd-dropdown" ref={listRef}>
            {actionRow && (
              <div
                className="sd-action-row"
                onMouseDown={(e) => {
                  e.preventDefault();          // keep focus off; don't toggle an option
                  setIsOpen(false);
                  setSearch('');
                  setHighlightIdx(-1);
                  actionRow.onClick();
                }}
              >
                {actionRow.label}
              </div>
            )}
            {renderOptions()}
          </div>
        </>
      )}
    </div>
  );
};

export default SearchableDropdown;
